import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import equal from 'fast-deep-equal/es6';
import dayjs from 'dayjs';
import { withRouter } from '../../WithRouter';
import { ProfileContext } from '../../../context/Profile.context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { entities, errors, reportingStandards, routes, urls, verifiedSectionKeys } from '../../../constants';
import { isSecurityComplete, isSecuritySlideValid } from '../../../utils/completes';
import { isDateWithinLast90Days, isDateWithinLastOneYear, isFutureDate, isSharesOutstandingLessThanAuthorized, isTierOtcid } from '../../../utils/helper';
import { isNumber } from '../../../utils/validations';
import Controls from '../../Controls';
import SectionNextActions from '../../SectionNextActions';
import { Award, FileBadge } from 'lucide-react';
import { Box, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, DateField, Dialog, DialogContent, DialogTitle, EntityLabel, EntityLabelGroup, InfoPanel, Label, NumberField, Typography } from '@otcmarketsgroup/otcm-ui-lib';

const UNDERLYING_NOTE = 'If this underlying security information is not up-to-date, please make a note on the Review and Submit Page.';
const NINETY_DAYS = 'Date must be within the last 90 days.';
const ONE_YEAR = 'Date must be within one year.';
const MAX_SHAREHOLDERS_LENGTH = 8;

const Securities = ({ params: { symbol }, saveApplication }) => {
  const [profileState, dispatchProfile] = useContext(ProfileContext);
  const [selectedSecurity, setSelectedSecurity] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const profileData = profileState.profile;
  const backupProfile = profileState.backupProfile;
  const securities = profileData && profileData.securityLists;
  const edgarFilingStatus = profileData.edgarFilingStatus;
  const companyTier = profileData.companyTier;
  const hasSingleFE = selectedSecurity && selectedSecurity.metadata && selectedSecurity.metadata.length === 1 && selectedSecurity.foreignExchangeSymbol && selectedSecurity.foreignExchangeName && selectedSecurity.metadata[0].symbol.length === 5;
  const hasMultipleFE = selectedSecurity && selectedSecurity.metadata && selectedSecurity.metadata.length > 1 && selectedSecurity.foreignExchangeSymbol && selectedSecurity.foreignExchangeName;
  const isReadOnly = profileState.readOnly;
  const currentSecurity = selectedSecurity || {};
  const today = dayjs();
  const isInternationalOrBank = !!(edgarFilingStatus && (edgarFilingStatus.includes(reportingStandards.INTERNATIONAL) || edgarFilingStatus.includes(reportingStandards.BANK)));
  const requiresShareholderFields = !isTierOtcid(companyTier) && !!edgarFilingStatus && !isInternationalOrBank;

  const singleFeLabel = <>
    Provide information for the underlying Security {selectedSecurity && selectedSecurity.foreignExchangeSymbol} on {selectedSecurity && selectedSecurity.foreignExchangeName}.
    <br /><br />
    {UNDERLYING_NOTE}
  </>;

  const mulipleFeLabel = <>
    Both of these ticker symbols are linked to {selectedSecurity && selectedSecurity.foreignExchangeSymbol} on the {selectedSecurity && selectedSecurity.foreignExchangeName}. Please provide the share information below for {selectedSecurity && selectedSecurity.foreignExchangeSymbol}.
    <br /><br />
    {UNDERLYING_NOTE}
  </>;

  useEffect(() => {
    if (!dialog) {
      setValidationError(false);
    }
  }, [dialog]);

  useEffect(() => {
    const isSecuritiesChange = !equal({ ...backupProfile.securityLists }, { ...securities });

    if (isSecuritiesChange) {
      saveApplication();
    }
  }, [backupProfile.securityLists, securities, saveApplication]);

  const entityTitles = item => {
    let titles = [entities.NO_NAME];
    const securityTitles = item.metadata && item.metadata.map(metadata => {
      return `${metadata.symbol}/${metadata.classname}`;
    });

    if (securityTitles && securityTitles.length > 0) titles = securityTitles.join(', ');
    return titles;
  };

  const handleEdit = security => {
    setSelectedSecurity({ ...security });
    setDialog(true);
  };

  const onFieldChange = (field, value) => {
    setSelectedSecurity(prev => {
      const updated = { ...(prev || {}), [field]: value };

      if (field === 'unlimitedAuthorizedShares' && value) {
        updated.authorizedShares = null;
      }

      if (field === 'noParValue' && value) {
        updated.parValue = null;
      }

      return updated;
    });
  };

  const onNumberFieldChange = (field, e) => {
    const { value } = e.target;
    const numberRegex = /^\d*$/;

    if (!numberRegex.test(value)) {
      return;
    }

    if (value === '') {
      onFieldChange(field, null);
      return;
    }

    const numericValue = Number(value);
    onFieldChange(field, Number.isNaN(numericValue) ? null : numericValue);
  };

  const onNumberFieldKeyDown = (field, e) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
      return;
    }

    e.preventDefault();

    const currentValue = selectedSecurity && selectedSecurity[field];

    if (!isNumber(currentValue)) {
      if (e.key === 'ArrowUp') {
        onFieldChange(field, 1);
      }

      return;
    }

    const delta = e.key === 'ArrowUp' ? 1 : -1;
    const nextValue = Math.max(0, currentValue + delta);
    onFieldChange(field, nextValue);
  };

  const onParValueFieldChange = e => {
    const { value } = e.target;

    // Allow only digits, a single decimal point, and up to 5 decimal places.
    if (!/^\d*(\.\d{0,5})?$/.test(value)) {
      return;
    }

    if (value === '') {
      onFieldChange('parValue', null);
      return;
    }

    onFieldChange('parValue', value);
  };

  const onUpdate = (data, field) => {
    dispatchProfile({
      type: 'UPDATE_PROFILE',
      payload: data,
      field: field
    });
  };

  const getDateErrorText = (dateValue, outOfRangeMessage, showValidation = true) => {
    if (!showValidation || !dateValue) return errors.INCOMPLETE_DATA_FIELD;
    if (isFutureDate(dateValue)) return errors.FUTURE_DATE;
    return outOfRangeMessage;
  };

  const handleUpdate = () => {
    setValidationError(false);

    let updateProvderList = profileData.securityLists;

    if (!isSecuritySlideValid(selectedSecurity, edgarFilingStatus, companyTier)) {
      setValidationError(true);
      return;
    }

    const normalizedSecurity = selectedSecurity && typeof selectedSecurity.parValue === 'string'
      ? {
        ...selectedSecurity,
        parValue: selectedSecurity.parValue === '.' ? null : Number(selectedSecurity.parValue)
      }
      : selectedSecurity;

    updateProvderList = updateProvderList.map(security => {
      if (security.metadata[0].symbol === selectedSecurity.metadata[0].symbol) return normalizedSecurity;

      return security;
    });

    onUpdate(updateProvderList, 'securityLists');

    setDialog(false);
  };
  return <Box display='flex' flexDirection='column' gap={2}>
    <Card>
      <CardHeader>
        <CardTitle icon={<Award />}>Securities</CardTitle>
      </CardHeader>
      <CardContent>
        {(!securities || securities.length === 0) && <Typography variant='body2'>
          No Securities have been added yet.
        </Typography>}
        <EntityLabelGroup>
          {securities && securities.map((security, i) => {
            const isComplete = isSecurityComplete(security, edgarFilingStatus, companyTier);

            return (
              <EntityLabel
                key={security?.metadata?.[0]?.symbol || i}
                title={entityTitles(security)}
                subtitle={isComplete ? 'Complete' : 'Enter Information'}
                icon={<FileBadge />}
                isComplete={isComplete}
                isWarning={!isComplete}
                onEntityClick={() => handleEdit(security)} />
            );
          })}
        </EntityLabelGroup>
      </CardContent>
    </Card>
    <SectionNextActions
      readOnly={isReadOnly}
      section={verifiedSectionKeys.SECURITIES}
      linkTo={`${routes.PROFILE_PREFIX}/${symbol}/${routes.REVIEW_SUBMIT}`}
    />
    <Dialog open={dialog} onClose={() => setDialog(false)}>
      <DialogTitle title={selectedSecurity && entityTitles(selectedSecurity)} onClose={() => setDialog(false)} />
      <DialogContent>
        <Box display='flex' flexDirection='column' gap={2}>
          {hasSingleFE && <InfoPanel title={singleFeLabel} />}
          {hasMultipleFE && <InfoPanel title={mulipleFeLabel} />}
          <InfoPanel title='Transfer Agent Verified' icon={<FontAwesomeIcon color="var(--otc-green)" icon={['fas', 'shield-halved']} />}>
            This icon denotes that this data has been recently provided by the Transfer Agent. Editing the information displayed here will remove the Transfer Agent Verified badge on this form, as well as on the Company Profile on otcmarkets.com
          </InfoPanel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant='h3'>Share Information</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 2 }}>
              <Box sx={{ gridColumn: 'span 4' }}>
                <Label startIcon={<FontAwesomeIcon size="xs" color="var(--otc-green)" icon={['fas', 'shield-halved']} />}>Shares Authorized</Label>
                <NumberField
                  showClearButton
                  placeholder={currentSecurity.unlimitedAuthorizedShares ? 'N/A' : 'XX'}
                  value={currentSecurity.unlimitedAuthorizedShares ? undefined : (currentSecurity.authorizedShares ?? '')}
                  readOnly={isReadOnly}
                  maxLength={15}
                  min={0}
                  maxDecimalPlaces={0}
                  showClearButton
                  showStepper
                  disabled={!!currentSecurity.unlimitedAuthorizedShares}
                  error={(validationError || isReadOnly) && !currentSecurity.unlimitedAuthorizedShares && !isNumber(currentSecurity.authorizedShares)}
                  helperText={(validationError || isReadOnly) && !currentSecurity.unlimitedAuthorizedShares && !isNumber(currentSecurity.authorizedShares) ? errors.INCOMPLETE_DATA_FIELD : ''}
                  onChange={e => onNumberFieldChange('authorizedShares', e)} />
              </Box>
              <Box sx={{ gridColumn: 'span 2' }}>
                {(() => {
                  const hasValue = !!currentSecurity.authorizedSharesAsOfDate;
                  const isInvalidRange = !isDateWithinLast90Days(currentSecurity.authorizedSharesAsOfDate);
                  const shouldShowError = (validationError && !hasValue) || (isFutureDate(currentSecurity.authorizedSharesAsOfDate) || isInvalidRange);

                  return <>
                    <Label tooltipSubtitle={NINETY_DAYS} tooltipDark>As of</Label>
                    <DateField
                      type='date'
                      maxDate={today}
                      showClearButton
                      value={currentSecurity.authorizedSharesAsOfDate || ''}
                      readOnly={isReadOnly}
                      error={shouldShowError}
                      helperText={shouldShowError ? getDateErrorText(currentSecurity.authorizedSharesAsOfDate, NINETY_DAYS) : ''}
                      onSelectDate={date => onFieldChange('authorizedSharesAsOfDate', date)} />
                  </>;
                })()}
              </Box>
              <Box sx={{ gridColumn: 'span 6' }}>
                <Checkbox
                  label='Unlimited Number of Shares Authorized'
                  checked={!!currentSecurity.unlimitedAuthorizedShares}
                  disabled={isReadOnly}
                  onChange={e => onFieldChange('unlimitedAuthorizedShares', e.target.checked)} />
              </Box>

              <Box sx={{ gridColumn: 'span 4' }}>
                <Label startIcon={<FontAwesomeIcon size="xs" color="var(--otc-green)" icon={['fas', 'shield-halved']} />}>Total Shares Outstanding</Label>
                <NumberField
                  showClearButton
                  showStepper
                  placeholder='XX'
                  showClearButton
                  maxDecimalPlaces={0}
                  maxLength={15}
                  min={0}
                  value={currentSecurity.outstandingShares ?? ''}
                  readOnly={isReadOnly}
                  error={(validationError || isReadOnly) && (!isNumber(currentSecurity.outstandingShares) || !isSharesOutstandingLessThanAuthorized(currentSecurity))}
                  helperText={(validationError || isReadOnly) && !isNumber(currentSecurity.outstandingShares) ? errors.INCOMPLETE_DATA_FIELD : (validationError || isReadOnly) && !isSharesOutstandingLessThanAuthorized(currentSecurity) ? 'Shares Outstanding cannot be greater than Number of Shares Authorized.' : ''}
                  onKeyDown={e => onNumberFieldKeyDown('outstandingShares', e)}
                  onChange={e => onNumberFieldChange('outstandingShares', e)} />
              </Box>
              <Box sx={{ gridColumn: 'span 2' }}>
                {(() => {
                  const hasValue = !!currentSecurity.outstandingSharesAsOfDate;
                  const isInvalidRange = !isDateWithinLast90Days(currentSecurity.outstandingSharesAsOfDate);
                  const shouldShowError = (validationError && !hasValue) || (isFutureDate(currentSecurity.outstandingSharesAsOfDate) || isInvalidRange);

                  return <>
                    <Label tooltipSubtitle={NINETY_DAYS} tooltipDark>As of</Label>
                    <DateField
                      type='date'
                      showClearButton
                      maxDate={today}
                      value={currentSecurity.outstandingSharesAsOfDate || ''}
                      readOnly={isReadOnly}
                      error={shouldShowError}
                      helperText={shouldShowError ? getDateErrorText(currentSecurity.outstandingSharesAsOfDate, NINETY_DAYS) : ''}
                      onSelectDate={date => onFieldChange('outstandingSharesAsOfDate', date)} />
                  </>;
                })()}
              </Box>

              <Box sx={{ gridColumn: 'span 4' }}>
                <Label>Beneficial Shareholders at least 100 shares</Label>
                <NumberField
                  showStepper
                  showClearButton
                  placeholder='XX'
                  value={currentSecurity.roundLotShareholders ?? ''}
                  readOnly={isReadOnly}
                  showClearButton
                  maxLength={8}
                  min={0}
                  maxDecimalPlaces={0}
                  error={(validationError || isReadOnly) && (currentSecurity.roundLotShareholdersDate || requiresShareholderFields) && !isNumber(currentSecurity.roundLotShareholders)}
                  helperText={(validationError || isReadOnly) && (currentSecurity.roundLotShareholdersDate || requiresShareholderFields) && !isNumber(currentSecurity.roundLotShareholders) ? errors.INCOMPLETE_DATA_FIELD : ''}
                  onKeyDown={e => onNumberFieldKeyDown('roundLotShareholders', e)}
                  onChange={e => onNumberFieldChange('roundLotShareholders', e)} />
                <Typography variant='caption'>
                  Beneficial Shareholder means any person who, directly or indirectly has or shares voting power of such security or investment power, which includes the power to dispose, or to direct the disposition of, such security.
                </Typography>
                <br />
                <Button variant='text' href={urls.GLOSSARY_BENEFICIAL_SHAREHOLDER} target='_blank' rel='noopener noreferrer'>See Beneficial Shareholders definition</Button>
              </Box>
              <Box sx={{ gridColumn: 'span 2' }}>
                {(() => {
                  const requiresDate = currentSecurity.roundLotShareholders || requiresShareholderFields;
                  const hasValue = !!currentSecurity.roundLotShareholdersDate;
                  const isInvalidRange = !isDateWithinLastOneYear(currentSecurity.roundLotShareholdersDate);
                  const shouldShowError = (requiresDate && validationError && !hasValue) || ((isFutureDate(currentSecurity.roundLotShareholdersDate) || isInvalidRange));

                  return <>
                    <Label tooltipSubtitle={ONE_YEAR} tooltipDark>As of</Label>
                    <DateField
                      type='date'
                      showClearButton
                      maxDate={today}
                      value={currentSecurity.roundLotShareholdersDate || ''}
                      readOnly={isReadOnly}
                      error={shouldShowError}
                      helperText={shouldShowError ? getDateErrorText(currentSecurity.roundLotShareholdersDate, ONE_YEAR) : ''}
                      onSelectDate={date => onFieldChange('roundLotShareholdersDate', date)} />
                  </>;
                })()}
              </Box>

              <Box sx={{ gridColumn: 'span 4' }}>
                <NumberField
                  showStepper
                  showClearButton
                  label='Par Value'
                  maxLength={15}
                  min={0}
                  maxDecimalPlaces={5}
                  format={{ maximumFractionDigits: 5 }}
                  placeholder={currentSecurity.noParValue ? 'N/A' : 'XX'}
                  value={currentSecurity.noParValue ? undefined : (currentSecurity.parValue ?? '')}
                  readOnly={isReadOnly}
                  disabled={!!currentSecurity.noParValue}
                  onChange={onParValueFieldChange} />
              </Box>
              <Box sx={{ gridColumn: 'span 2', display: 'flex', alignItems: 'end' }}>
                <Checkbox
                  label='No Par Value'
                  checked={!!currentSecurity.noParValue}
                  disabled={isReadOnly}
                  onChange={e => onFieldChange('noParValue', e.target.checked)} />
              </Box>

              <Box sx={{ gridColumn: 'span 4' }}>
                <NumberField
                  showStepper
                  showClearButton
                  maxLength={15}
                  min={0}
                  maxDecimalPlaces={0}
                  placeholder='XX'
                  label='Public Float'
                  value={currentSecurity.publicFloat ?? ''}
                  readOnly={isReadOnly}
                  error={(validationError || isReadOnly) && (currentSecurity.publicFloatAsOfDate || requiresShareholderFields) && !isNumber(currentSecurity.publicFloat)}
                  helperText={(validationError || isReadOnly) && (currentSecurity.publicFloatAsOfDate || requiresShareholderFields) && !isNumber(currentSecurity.publicFloat) ? errors.INCOMPLETE_DATA_FIELD : ''}
                  onKeyDown={e => onNumberFieldKeyDown('publicFloat', e)}
                  onChange={e => onNumberFieldChange('publicFloat', e)} />
                <Typography variant='caption'>
                  Public Float refers to the total number of unrestricted shares not held directly or indirectly by an officer, director, any person who is the beneficial owner of more than 10 percent of the total shares outstanding (a "10% Control Person"), or any Affiliates thereof, or any Family Members of officers, directors and 10% Control Persons.
                </Typography>
                <br />
                <Button variant='text' href={urls.GLOSSARY_PUBLIC_FLOAT} target='_blank' rel='noopener noreferrer'>See Public Float Calculation</Button>
              </Box>
              <Box sx={{ gridColumn: 'span 2' }}>
                {(() => {
                  const requiresDate = currentSecurity.publicFloat || requiresShareholderFields;
                  const hasValue = !!currentSecurity.publicFloatAsOfDate;
                  const isInvalidRange = !isDateWithinLastOneYear(currentSecurity.publicFloatAsOfDate);
                  const shouldShowError = (requiresDate && validationError && !hasValue) || ((isFutureDate(currentSecurity.publicFloatAsOfDate) || isInvalidRange));

                  return <>
                    <Label tooltipSubtitle={ONE_YEAR} tooltipDark>As of</Label>
                    <DateField
                      type='date'
                      showClearButton
                      maxDate={today}
                      value={currentSecurity.publicFloatAsOfDate || ''}
                      readOnly={isReadOnly}
                      error={shouldShowError}
                      helperText={shouldShowError ? getDateErrorText(currentSecurity.publicFloatAsOfDate, ONE_YEAR) : ''}
                      onSelectDate={date => onFieldChange('publicFloatAsOfDate', date)} />
                  </>;
                })()}
              </Box>
            </Box>
          </Box>
          {validationError && <InfoPanel isWarning title={errors.INCOMPLETE_DATA} />}
        </Box>
      </DialogContent>
      {!isReadOnly && <Controls
        cancelText='Cancel'
        submitText='Update'
        onCancelClick={() => setDialog(false)}
        onSubmitClick={handleUpdate}
      />}
    </Dialog>
  </Box>;
};

Securities.propTypes = {
  params: PropTypes.shape({
    symbol: PropTypes.string
  }),
  saveApplication: PropTypes.func
};

export default withRouter(Securities);
